import { createFileRoute } from "@tanstack/react-router";
import SeoLandingPage, {
  buildFaqSchema,
  buildLocalBusinessSchema,
} from "@/components/SeoLandingPage";
import heroImage from "@/assets/mercedes-benz-stadium-sprinter-limo-party-bus.jpg?format=webp&quality=72&w=1400&imagetools";
import midImage from "@/assets/sprinter-bar.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryA from "@/assets/sprinter-limo-3.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryB from "@/assets/sprinter-exec-amber.jpg?format=webp&quality=72&w=1400&imagetools";
import galleryC from "@/assets/georgia-florida-halloween-sprinter-limo-atlanta.jpg?format=webp&quality=72&w=1400&imagetools";

const PAGE_URL = "https://atlantaluxurysprinters.com/concert-sports-transportation-atlanta";
const PAGE_TITLE = "Concert & Sports Transportation Atlanta | Sprinter Limo";
const PAGE_DESC =
  "Chauffeured Sprinter Limousines to Mercedes-Benz Stadium, State Farm Arena, and Truist Park. Door drop, staged pickup, no parking or surge pricing. Call (678) 431-7222.";

const faqs = [
  {
    q: "Where does the chauffeur drop us off at Mercedes-Benz Stadium?",
    a: "Drop-off depends on the event and the gate assignments on your tickets, but we typically use the Northside Drive or Andrew Young International drop zones, which put you within a short walk of Gates 1 and 2. Your chauffeur confirms the exact point the morning of, because Atlanta Police adjust closures for large events.",
  },
  {
    q: "How do we find the vehicle after the event?",
    a: "You do not hunt for it. Your chauffeur is staged at a pickup point agreed before the event, usually a hotel entrance or a garage two to four blocks from the venue crush, and texts you the location and the walking route at the start of the final quarter or the encore.",
  },
  {
    q: "Is it booked hourly or as a round trip?",
    a: "Both work. A round trip is simplest for a single-venue evening. An hourly block makes more sense when you are adding dinner before, drinks after, or a second venue, because the chauffeur and vehicle stay with your group the entire time. Hourly blocks have a 4-hour minimum.",
  },
  {
    q: "Can we drink in the vehicle on the way to the game?",
    a: "Passengers of legal drinking age may, and the wet bar has ice wells and chilled storage. Bring what you like, we do not stock or sell alcohol. Georgia law keeps the chauffeur's partition of the vehicle separate, and open containers stay in the cabin.",
  },
  {
    q: "How early should we leave for a Truist Park game?",
    a: "Ninety minutes before first pitch from in-town Atlanta. The Battery draws a crowd well before the game itself, and I-75 northbound past Cumberland is the single most unpredictable stretch of any event route we run.",
  },
  {
    q: "Do you handle groups larger than 14?",
    a: "Yes, with multiple Mercedes-Benz Sprinter Limousines travelling as a convoy under one lead chauffeur, so your group arrives together rather than trickling in across twenty minutes.",
  },
  {
    q: "What does a chauffeured concert night cost?",
    a: "Sprinters start from $250/hour. I quote a fixed hourly or round-trip rate in writing before you book, and it stays the same no matter how many people leave the arena at once.",
  },
];

export const Route = createFileRoute("/concert-sports-transportation-atlanta")({
  head: () => ({
    meta: [
      { title: PAGE_TITLE },
      { name: "description", content: PAGE_DESC },
      {
        name: "keywords",
        content:
          "concert transportation atlanta, sports transportation atlanta, limo to mercedes-benz stadium, state farm arena transportation, truist park limo service, atlanta game day car service, sprinter limo concert atlanta",
      },
      { property: "og:title", content: PAGE_TITLE },
      { property: "og:description", content: PAGE_DESC },
      { property: "og:type", content: "website" },
      { property: "og:url", content: PAGE_URL },
      { property: "og:image", content: heroImage },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: PAGE_TITLE },
      { name: "twitter:description", content: PAGE_DESC },
      { name: "twitter:image", content: heroImage },
    ],
    links: [{ rel: "canonical", href: PAGE_URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(
          buildLocalBusinessSchema({
            url: PAGE_URL,
            name: "Atlanta Luxury Sprinters, Concert & Sports Transportation",
            image: heroImage,
            description: PAGE_DESC,
            serviceType: "Concert and Sporting Event Transportation",
            areaServed: [
              { type: "City", name: "Atlanta" },
              { type: "City", name: "Buckhead" },
              { type: "City", name: "Cumberland" },
              { type: "City", name: "Alpharetta" },
            ],
          }),
        ),
      },
      { type: "application/ld+json", children: JSON.stringify(buildFaqSchema(faqs)) },
    ],
  }),
  component: Page,
});

function Page() {
  return (
    <SeoLandingPage
      eyebrow="Concerts • Games • Atlanta Venues"
      h1="Concert & Sports Transportation in Atlanta"
      intro="You've planned this night. I'll handle the drive to Mercedes-Benz Stadium, State Farm Arena, Truist Park or your Atlanta amphitheater, with a pickup plan for when it's over."
      heroImage={heroImage}
      heroAlt="Mercedes-Benz Sprinter Limousine cabin lit for a night out at an Atlanta stadium event"
      midImage={midImage}
      midAlt="Wet bar and lounge seating inside a Mercedes-Benz Sprinter Limousine in Atlanta"
      sectionOneTitle="Your Evening, Without the Parking Plan"
      sectionOneBody={[
        "Your tickets are booked. Let my chauffeur handle the downtown traffic and parking while your group travels together.",
        "We plan drop-off around your ticket entrance and road closures. Your chauffeur waits outside the closure area and texts your pickup location and walking route before the event ends.",
        "I'll confirm your rate in writing before you book.",
      ]}
      bulletsTitle="How Event Nights Work"
      bulletsIntro="One chauffeur, one vehicle, and a pickup point agreed before you ever leave the house."
      bullets={[
        "Gate-specific drop-off matched to your ticket entrance",
        "Post-event staging point outside the road closure ring",
        "Chauffeur texts the pickup location and walking route before the event ends",
        "Fixed rate confirmed in writing, no surge, no parking, no garage fees",
        "Wet bar with ice wells; bring your own for the ride in",
        "Multi-vehicle convoys for groups over 14, travelling together",
        "Dinner and after-party stops built into an hourly block",
      ]}
      coverageTitle="Atlanta Venues We Run Regularly"
      coverageIntro="Stadiums, arenas, amphitheaters, and the neighborhoods our event groups leave from."
      coverage={[
        "Mercedes-Benz Stadium",
        "State Farm Arena",
        "Truist Park & The Battery",
        "Center Stage & The Tabernacle",
        "Fox Theatre",
        "Coca-Cola Roxy",
        "Ameris Bank Amphitheatre, Alpharetta",
        "Cadence Bank Amphitheatre, Chastain Park",
        "Gas South Arena, Duluth",
        "Bobby Dodd Stadium",
        "Buckhead & Midtown pickups",
        "Sandy Springs & Alpharetta pickups",
      ]}
      routeTable={{
        title: "Event Night Routes and Timing",
        intro:
          "Leave times we recommend from in-town Atlanta, based on the traffic our chauffeurs actually sit in on event nights.",
        rows: [
          {
            route: "Buckhead → Mercedes-Benz Stadium",
            distance: "~10 mi",
            time: "25–50 min",
            notes: "Leave 90 min before kickoff. Northside Dr drop; closures shift by event.",
          },
          {
            route: "Buckhead → State Farm Arena",
            distance: "~9 mi",
            time: "25–45 min",
            notes: "Connector southbound. Centennial Olympic Park Dr drop is the reliable one.",
          },
          {
            route: "Midtown → Truist Park / The Battery",
            distance: "~13 mi",
            time: "25–45 min",
            notes: "I-75 N past Cumberland is the wildcard. Arrive 90 min before first pitch.",
          },
          {
            route: "Downtown → Fox Theatre",
            distance: "~2 mi",
            time: "10–20 min",
            notes: "Peachtree St drop at the marquee; pickup staged on Ponce de Leon after curtain.",
          },
          {
            route: "Atlanta → Ameris Bank Amphitheatre, Alpharetta",
            distance: "~26 mi",
            time: "35–60 min",
            notes: "GA-400 N. Lawn shows empty all at once, staged pickup is essential here.",
          },
          {
            route: "Atlanta → Chastain Park Amphitheatre",
            distance: "~11 mi",
            time: "20–40 min",
            notes: "Neighborhood streets close early. Drop at Powers Ferry Rd, pickup on Lake Forest.",
          },
          {
            route: "Atlanta → Gas South Arena, Duluth",
            distance: "~28 mi",
            time: "35–55 min",
            notes: "I-85 N. Sugarloaf Pkwy backs up 45 min before doors on sold-out nights.",
          },
          {
            route: "Dinner → venue → after-party (hourly block)",
            distance: "Varies",
            time: "5–6 hr block",
            notes: "Chauffeur waits on-site between stops; nothing is re-booked mid-evening.",
          },
        ],
        footnote:
          "Post-event departures are staged outside the closure ring, expect a short walk to your vehicle.",
      }}
      detailSections={[
        {
          title: "Game Day at Mercedes-Benz Stadium and Truist Park",
          body: [
            "Atlanta closes streets around both venues well before the event starts, and the closure map is not the same for a Falcons game, an Atlanta United match, and a stadium concert. Our chauffeurs confirm the day's plan the morning of rather than assuming last month's route.",
          ],
          list: [
            "Falcons and Atlanta United, Northside Drive drop, staged pickup west of the closure ring.",
            "Stadium concerts, earlier arrival than a game; merchandise and security lines run long.",
            "Braves at Truist Park, leave 90 minutes early; The Battery fills before the gates do.",
            "College football at Bobby Dodd, campus closures push drops to North Ave.",
            "Playoff and sold-out nights, we add 20 minutes to every leg by default.",
          ],
        },
        {
          title: "Concerts, Theatre, and Amphitheater Nights",
          body: [
            "Amphitheaters are the strongest case for a chauffeur in Atlanta. Chastain and Ameris Bank both empty their entire audience into a residential street grid within ten minutes of the last song, and both have parking that empties slower than the lawn does.",
            "We stage outside that grid, send you the walking route before the encore, and have you moving while the lot is still stationary.",
          ],
        },
        {
          title: "Which Vehicle for Your Group",
          list: [
            "2–4 passengers, Suburban SUV, quickest drop and simplest staging.",
            "Up to 13 passengers, Executive Sprinter with facing captain's chairs.",
            "9–14 passengers, Mercedes-Benz Sprinter Limousine with wet bar and lounge seating.",
            "15+ passengers, multiple Sprinters as a convoy under one lead chauffeur.",
          ],
        },
      ]}
      gallery={{
        title: "Event Night Fleet",
        intro: "Sprinter Limousines with wet bar, starlight headliner, and lounge seating for groups up to 14.",
        images: [
          { src: galleryA, alt: "Mercedes-Benz Sprinter Limousine interior for an Atlanta concert night", caption: "Sprinter Limousine, lounge seating" },
          { src: galleryB, alt: "Executive Sprinter cabin for smaller Atlanta event groups", caption: "Executive Sprinter, up to 13" },
          { src: galleryC, alt: "Illuminated Sprinter Limousine cabin on an Atlanta event night", caption: "Cabin lighting, passenger controlled" },
        ],
      }}
      faqs={faqs}
      faqTitle="Atlanta Event Transportation FAQs"
      relatedTitle="Related Occasion Services"
      related={[
        { to: "/uga-game-day-transportation", title: "UGA Game Day Transportation", desc: "Custom Sprinter limo from Atlanta to Athens and Sanford Stadium." },
        { to: "/mercedes-benz-stadium-transportation", title: "Mercedes-Benz Stadium Transportation", desc: "Falcons, SEC Championship and concerts in a Sprinter limo." },
        { to: "/wedding-transportation-atlanta", title: "Wedding Transportation Atlanta", desc: "Bridal party, couple's transfer, and guest loops." },
        { to: "/winery-tour-transportation-atlanta", title: "Winery & Brewery Tours", desc: "Dahlonega wine trail and Atlanta brewery loops." },
        { to: "/sprinter-limo-atlanta", title: "Sprinter Limo Atlanta", desc: "The full Sprinter Limousine fleet and configurations." },
      ]}
      ctaTitle="Text Me Your Event Plans"
      ctaBody="Text me the event, date, pickup and group size. Include dinner or after-party stops, and I'll send your quote in writing."
    />
  );
}
